import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Checkbox,
  Paper,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Controller } from 'react-hook-form';
import { TextBox } from '@voicecare.ai/ui-library';
import { useTheme } from '../../ThemeContext';
import { getFacilities, getDepartmentsByFacilityId } from '../../utils/apiClient';
import ThemedChip from '../common/ThemedChip';

/**
 * FacilityDepartmentAssignment Component
 * 
 * New design matching Figma mockup with left/right panel layout
 * Left: Facilities list with search
 * Right: Departments for selected facility with role input
 */
const FacilityDepartmentAssignment = ({
  fieldName,
  control,
  errors,
  fieldError,
  hasError,
  watch,
  setValue,
  getValues,
  disabled = false
}) => {
  const { currentTheme, themeMode } = useTheme();
  const [facilities, setFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [facilitySearch, setFacilitySearch] = useState('');
  const [roleInputs, setRoleInputs] = useState({}); // Store role input per department
  
  // Watch the affiliations array
  const affiliations = watch(fieldName) || [];

  /**
   * Fetch all facilities on mount
   */
  useEffect(() => {
    const fetchFacilitiesData = async () => {
      try {
        const facilitiesData = await getFacilities();
        setFacilities(facilitiesData || []);
      } catch (error) {
        console.error('Error fetching facilities:', error);
        setFacilities([]);
      }
    };

    fetchFacilitiesData();
  }, []);

  /**
   * Fetch departments when a facility is selected
   */
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!selectedFacilityId) {
        setDepartments([]);
        setRoleInputs({}); // Clear role inputs when no facility selected
        return;
      }

      setLoadingDepartments(true);
      try {
        const response = await getDepartmentsByFacilityId(selectedFacilityId);
        
        // Handle different response formats
        let departmentsData = [];
        if (response) {
          if (Array.isArray(response)) {
            departmentsData = response;
          } else if (response.response && Array.isArray(response.response)) {
            departmentsData = response.response;
          } else if (response.data && Array.isArray(response.data)) {
            departmentsData = response.data;
          }
        }
        
        setDepartments(departmentsData);
        setRoleInputs({}); // Clear role inputs when facility changes
      } catch (error) {
        console.error('Error fetching departments:', error);
        setDepartments([]);
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, [selectedFacilityId]);

  /**
   * Handle facility selection - just select, don't add to affiliations yet
   */
  const handleFacilitySelect = (facilityId) => {
    console.log('🏥 Facility Selected:', facilityId);
    setSelectedFacilityId(facilityId);
    
    const currentAffiliations = getValues(fieldName) || [];
    console.log('Current affiliations:', currentAffiliations);
    
    // Don't automatically add affiliation - wait for user to check departments or add roles
  };

  /**
   * Handle department checkbox toggle
   */
  const handleDepartmentToggle = (departmentId) => {
    const currentAffiliations = getValues(fieldName) || [];
    
    console.log('🔘 Department Toggle:', {
      departmentId,
      selectedFacilityId,
      currentAffiliationsCount: currentAffiliations.length,
      currentAffiliations
    });
    
    // Check if this facility-department combination exists
    const existingIndex = currentAffiliations.findIndex(
      aff => aff.facilityId === selectedFacilityId && aff.departmentId === departmentId
    );

    if (existingIndex !== -1) {
      // Remove the affiliation
      console.log('❌ Removing department affiliation at index:', existingIndex);
      const updatedAffiliations = currentAffiliations.filter((_, idx) => idx !== existingIndex);
      setValue(fieldName, updatedAffiliations.length > 0 ? updatedAffiliations : [{ facilityId: '', departmentId: '', role: [] }]);
      console.log('Updated affiliations after removal:', updatedAffiliations);
    } else {
      // Add new department affiliation
      const newAffiliation = {
        facilityId: selectedFacilityId,
        departmentId: departmentId,
        role: []
      };
      
      // Remove empty facility-level affiliation if it exists (since we're adding a department now)
      const filteredAffiliations = currentAffiliations.filter(
        aff => !(aff.facilityId === selectedFacilityId && (!aff.departmentId || aff.departmentId === '') && (!aff.role || aff.role.length === 0))
      );
      
      console.log('✅ Adding new department affiliation:', newAffiliation);
      console.log('Removed empty facility-level affiliations for this facility');
      setValue(fieldName, [...filteredAffiliations, newAffiliation]);
      console.log('Updated affiliations after addition:', [...filteredAffiliations, newAffiliation]);
    }
  };

  /**
   * Check if a department is selected
   */
  const isDepartmentSelected = (departmentId) => {
    return affiliations.some(
      aff => aff.facilityId === selectedFacilityId && aff.departmentId === departmentId
    );
  };

  /**
   * Get count of selected departments (excluding facility-level)
   */
  const getSelectedCount = () => {
    if (!selectedFacilityId) return 0;
    // Only count department-level affiliations (where departmentId is not empty)
    const departmentAffs = affiliations.filter(
      aff => aff.facilityId === selectedFacilityId && aff.departmentId && aff.departmentId !== ''
    );
    return departmentAffs.length;
  };

  /**
   * Handle role removal from specific affiliation
   */
  const handleRemoveRole = (affiliationIndex, roleToRemove) => {
    const currentAffiliations = getValues(fieldName) || [];
    const updatedAffiliations = [...currentAffiliations];
    
    if (updatedAffiliations[affiliationIndex]) {
      updatedAffiliations[affiliationIndex].role = 
        (updatedAffiliations[affiliationIndex].role || []).filter(r => r !== roleToRemove);
      setValue(fieldName, updatedAffiliations);
    }
  };

  /**
   * Add role to specific department
   */
  const handleAddRoleToDepartment = (departmentId, roleValue) => {
    const trimmedRole = roleValue.trim();
    if (!trimmedRole || !selectedFacilityId) return;
    
    console.log('➕ Adding role to department:', {
      departmentId,
      role: trimmedRole,
      selectedFacilityId
    });
    
    const currentAffiliations = getValues(fieldName) || [];
    let roleAdded = false;
    
    const updatedAffiliations = currentAffiliations.map(aff => {
      if (aff.facilityId === selectedFacilityId && aff.departmentId === departmentId) {
        const existingRoles = aff.role || [];
        if (!existingRoles.includes(trimmedRole)) {
          roleAdded = true;
          console.log('✅ Role added successfully to affiliation:', {
            facilityId: aff.facilityId,
            departmentId: aff.departmentId,
            oldRoles: existingRoles,
            newRoles: [...existingRoles, trimmedRole]
          });
          return {
            ...aff,
            role: [...existingRoles, trimmedRole]
          };
        } else {
          console.log('⚠️ Role already exists, skipping');
        }
      }
      return aff;
    });
    
    if (!roleAdded) {
      console.log('❌ No affiliation found to add role to. Current affiliations:', currentAffiliations);
    }
    
    setValue(fieldName, updatedAffiliations);
    console.log('Final affiliations after role addition:', updatedAffiliations);
    
    // Clear the input for this department
    setRoleInputs(prev => ({
      ...prev,
      [departmentId]: ''
    }));
  };

  /**
   * Filter and sort facilities by search
   * Selected facilities (with affiliations) appear at the top
   */
  const filteredFacilities = facilities
    .filter(facility =>
      facility.facilityName?.toLowerCase().includes(facilitySearch.toLowerCase()) ||
      facility.location?.streetAddress?.toLowerCase().includes(facilitySearch.toLowerCase())
    )
    .sort((a, b) => {
      // Check if facilities have affiliations
      const aHasAffiliations = affiliations.some(aff => aff.facilityId === a._id);
      const bHasAffiliations = affiliations.some(aff => aff.facilityId === b._id);
      
      // Sort: facilities with affiliations first
      if (aHasAffiliations && !bHasAffiliations) return -1;
      if (!aHasAffiliations && bHasAffiliations) return 1;
      
      // If both have or both don't have affiliations, maintain original order
      return 0;
    });

  /**
   * Clear selected facility and departments
   */
  const handleClearSelection = () => {
    if (!selectedFacilityId) return;
    
    const currentAffiliations = getValues(fieldName) || [];
    const updatedAffiliations = currentAffiliations.filter(
      aff => aff.facilityId !== selectedFacilityId
    );
    
    setValue(fieldName, updatedAffiliations.length > 0 ? updatedAffiliations : [{ facilityId: '', departmentId: '', role: [] }]);
    setSelectedFacilityId(null);
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field, fieldState }) => (
        <Box sx={{ width: '100%' }}>
          {/* Two-column layout: Facilities | Departments */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 3
          }}>
            {/* LEFT PANEL: Facilities */}
            <Paper
              elevation={0}
              sx={{
                backgroundColor: themeMode === 'dark' ? '#1a1836' : '#ffffff',
                border: `1px solid ${currentTheme.border.primary}`,
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                height: '500px',
                overflow: 'hidden'
              }}
            >
              {/* Sticky Header: Title + Search */}
              <Box sx={{ 
                p: 2,
                pb: 1,
                backgroundColor: themeMode === 'dark' ? '#1a1836' : '#ffffff',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderBottom: `1px solid ${currentTheme.border.primary}`
              }}>
                <Typography variant="subtitle2" sx={{ 
                  color: currentTheme.text.primary, 
                  fontWeight: 600,
                  fontSize: '14px',
                  mb: 2
                }}>
                  Facilities
                </Typography>

                {/* Search Input */}
                <Box>
                  <TextBox
                    type="text"
                    placeholder="Search..."
                    value={facilitySearch}
                    onChange={(e) => setFacilitySearch(e.target.value)}
                    themeMode={themeMode}
                    textBoxText={currentTheme.loginPage.textBoxText}
                    textBoxBackground={themeMode === 'dark' ? currentTheme.loginPage.textBoxBackground : '#ffffff'}
                    style={{
                      width: '100%',
                      height: '36px',
                      fontSize: '13px'
                    }}
                    startAdornment={
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: currentTheme.text.secondary, fontSize: '18px' }} />
                      </InputAdornment>
                    }
                  />
                </Box>
              </Box>

              {/* Scrollable Facilities List */}
              <Box sx={{ 
                flex: 1, 
                overflow: 'auto',
                p: 2,
                pt: 1,
                // Custom scrollbar
                scrollbarWidth: 'thin',
                scrollbarColor: `${themeMode === 'dark' ? '#666' : '#888'} transparent`,
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: themeMode === 'dark' ? '#363354' : '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: themeMode === 'dark' ? '#666' : '#888',
                  borderRadius: '3px',
                },
              }}>
                {filteredFacilities.length === 0 ? (
                  <Typography sx={{ 
                    color: currentTheme.text.secondary, 
                    fontSize: '13px',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    py: 2
                  }}>
                    No facilities found
                  </Typography>
                ) : (
                  filteredFacilities.map((facility) => {
                    // Check if facility has any roles assigned
                    const facilityAffiliations = affiliations.filter(aff => aff.facilityId === facility._id);
                    const hasRoles = facilityAffiliations.some(aff => aff.role && aff.role.length > 0);
                    const isSelected = selectedFacilityId === facility._id;
                    
                    return (
                      <Box
                        key={facility._id}
                        onClick={() => handleFacilitySelect(facility._id)}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          cursor: 'pointer',
                          borderRadius: '6px',
                          border: isSelected
                            ? '2px solid #FF6B47' 
                            : hasRoles
                            ? '2px solid #4CAF50'
                            : `1px solid ${currentTheme.border.primary}`,
                          backgroundColor: isSelected
                            ? (themeMode === 'dark' ? 'rgba(255, 107, 71, 0.1)' : 'rgba(255, 107, 71, 0.05)')
                            : hasRoles
                            ? (themeMode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)')
                            : (themeMode === 'dark' ? '#04002b' : '#f8f9fa'),
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: themeMode === 'dark' ? 'rgba(255, 107, 71, 0.15)' : 'rgba(255, 107, 71, 0.08)',
                            borderColor: '#FF6B47'
                          }
                        }}
                      >
                      <Typography sx={{ 
                        color: currentTheme.text.primary, 
                        fontSize: '14px',
                        fontWeight: selectedFacilityId === facility._id ? 600 : 500,
                        mb: 0.5
                      }}>
                        {facility.facilityName}
                      </Typography>
                      <Typography sx={{ 
                        color: currentTheme.text.secondary, 
                        fontSize: '12px'
                      }}>
                        {(() => {
                          // Only count department-level affiliations (not facility-level)
                          const deptCount = affiliations.filter(
                            aff => aff.facilityId === facility._id && aff.departmentId && aff.departmentId !== ''
                          ).length;
                          const facilityHasRoles = affiliations.some(
                            aff => aff.facilityId === facility._id && 
                                   (!aff.departmentId || aff.departmentId === '') && 
                                   aff.role && aff.role.length > 0
                          );
                          
                          if (deptCount > 0) {
                            return `${deptCount} department${deptCount !== 1 ? 's' : ''} selected`;
                          } else if (facilityHasRoles) {
                            return 'Facility-level roles added';
                          } else {
                            return facility.location?.streetAddress || 'No address';
                          }
                        })()}
                      </Typography>
                    </Box>
                  );
                  })
                )}
              </Box>
            </Paper>

            {/* RIGHT PANEL: Departments */}
            <Paper
              elevation={0}
              sx={{
                backgroundColor: themeMode === 'dark' ? '#1a1836' : '#ffffff',
                border: `1px solid ${currentTheme.border.primary}`,
                borderRadius: '8px',
                display: 'flex',
                flexDirection: 'column',
                height: '500px',
                overflow: 'hidden'
              }}
            >
              {/* Sticky Header */}
              <Box sx={{ 
                p: 2,
                pb: 1,
                backgroundColor: themeMode === 'dark' ? '#1a1836' : '#ffffff',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderBottom: `1px solid ${currentTheme.border.primary}`,
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center'
              }}>
                <Typography variant="subtitle2" sx={{ 
                  color: currentTheme.text.primary, 
                  fontWeight: 600,
                  fontSize: '14px'
                }}>
                  Departments {selectedFacilityId ? `(${getSelectedCount()} selected)` : '(0 selected)'}
                </Typography>
                
                {selectedFacilityId && getSelectedCount() > 0 && (
                  <Box
                    onClick={handleClearSelection}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer',
                      color: currentTheme.text.secondary,
                      fontSize: '12px',
                      '&:hover': {
                        color: '#e63e2b'
                      }
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '16px' }} />
                    <Typography sx={{ fontSize: '12px' }}>All</Typography>
                  </Box>
                )}
              </Box>

              {!selectedFacilityId ? (
                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 2
                }}>
                  <Typography sx={{ 
                    color: currentTheme.text.secondary, 
                    fontSize: '13px',
                    fontStyle: 'italic',
                    textAlign: 'center'
                  }}>
                    No facilities selected<br />Select facilities from the left to continue
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  flex: 1, 
                  overflow: 'auto',
                  p: 2,
                  pt: 1,
                  // Custom scrollbar
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${themeMode === 'dark' ? '#666' : '#888'} transparent`,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: themeMode === 'dark' ? '#363354' : '#f1f1f1',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: themeMode === 'dark' ? '#666' : '#888',
                    borderRadius: '3px',
                  },
                }}>
                  {loadingDepartments ? (
                    <Typography sx={{ 
                      color: currentTheme.text.secondary, 
                      fontSize: '13px',
                      fontStyle: 'italic',
                      textAlign: 'center',
                      py: 2
                    }}>
                      Loading departments...
                    </Typography>
                  ) : departments.length === 0 ? (
                    // Facility has no departments - show facility-level role input
                    <Box>
                      <Typography sx={{ 
                        color: currentTheme.text.primary, 
                        fontSize: '14px',
                        fontWeight: 600,
                        mb: 2
                      }}>
                        Facility Level Roles
                      </Typography>
                      
                      {/* Role Input */}
                      <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        alignItems: 'center', 
                        mb: 2,
                        flexWrap: 'nowrap',
                        width: '100%'
                      }}>
                        <input
                          type="text"
                          placeholder="Add role (e.g., Physician, Consultant)"
                          value={roleInputs['facility-' + selectedFacilityId] || ''}
                          onChange={(e) => setRoleInputs(prev => ({
                            ...prev,
                            ['facility-' + selectedFacilityId]: e.target.value
                          }))}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const trimmedRole = (roleInputs['facility-' + selectedFacilityId] || '').trim();
                              if (!trimmedRole) return;
                              
                              console.log('➕ Adding facility-level role:', trimmedRole);
                              
                              // Add role to facility-level affiliation
                              const currentAffiliations = getValues(fieldName) || [];
                              
                              // Check if facility-level affiliation exists
                              const facilityAffIndex = currentAffiliations.findIndex(
                                aff => aff.facilityId === selectedFacilityId && (!aff.departmentId || aff.departmentId === '')
                              );
                              
                              let updatedAffiliations;
                              if (facilityAffIndex !== -1) {
                                // Update existing facility-level affiliation
                                updatedAffiliations = currentAffiliations.map((aff, idx) => {
                                  if (idx === facilityAffIndex) {
                                    const existingRoles = aff.role || [];
                                    if (!existingRoles.includes(trimmedRole)) {
                                      return { ...aff, role: [...existingRoles, trimmedRole] };
                                    }
                                  }
                                  return aff;
                                });
                              } else {
                                // Create new facility-level affiliation
                                const newAffiliation = {
                                  facilityId: selectedFacilityId,
                                  departmentId: '',
                                  role: [trimmedRole]
                                };
                                updatedAffiliations = [...currentAffiliations, newAffiliation];
                                console.log('✅ Created new facility-level affiliation with role');
                              }
                              
                              setValue(fieldName, updatedAffiliations);
                              setRoleInputs(prev => ({
                                ...prev,
                                ['facility-' + selectedFacilityId]: ''
                              }));
                            }
                          }}
                          disabled={disabled}
                          style={{
                            flex: '1 1 auto',
                            minWidth: 0,
                            padding: '8px 10px',
                            border: `1px solid ${currentTheme.border?.primary || '#ddd'}`,
                            borderRadius: '6px',
                            fontSize: '12px',
                            backgroundColor: themeMode === 'dark' ? '#2d2a45' : '#ffffff',
                            color: currentTheme.text?.primary || '#333',
                            outline: 'none'
                          }}
                        />
                        <button
                          onClick={() => {
                            const trimmedRole = (roleInputs['facility-' + selectedFacilityId] || '').trim();
                            if (!trimmedRole) return;
                            
                            console.log('➕ Adding facility-level role (button click):', trimmedRole);
                            
                            // Add role to facility-level affiliation
                            const currentAffiliations = getValues(fieldName) || [];
                            
                            // Check if facility-level affiliation exists
                            const facilityAffIndex = currentAffiliations.findIndex(
                              aff => aff.facilityId === selectedFacilityId && (!aff.departmentId || aff.departmentId === '')
                            );
                            
                            let updatedAffiliations;
                            if (facilityAffIndex !== -1) {
                              // Update existing facility-level affiliation
                              updatedAffiliations = currentAffiliations.map((aff, idx) => {
                                if (idx === facilityAffIndex) {
                                  const existingRoles = aff.role || [];
                                  if (!existingRoles.includes(trimmedRole)) {
                                    return { ...aff, role: [...existingRoles, trimmedRole] };
                                  }
                                }
                                return aff;
                              });
                            } else {
                              // Create new facility-level affiliation
                              const newAffiliation = {
                                facilityId: selectedFacilityId,
                                departmentId: '',
                                role: [trimmedRole]
                              };
                              updatedAffiliations = [...currentAffiliations, newAffiliation];
                              console.log('✅ Created new facility-level affiliation with role');
                            }
                            
                            setValue(fieldName, updatedAffiliations);
                            setRoleInputs(prev => ({
                              ...prev,
                              ['facility-' + selectedFacilityId]: ''
                            }));
                          }}
                          disabled={disabled || !(roleInputs['facility-' + selectedFacilityId] || '').trim()}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: disabled || !(roleInputs['facility-' + selectedFacilityId] || '').trim() ? '#ccc' : '#FF6B47',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '500',
                            cursor: disabled || !(roleInputs['facility-' + selectedFacilityId] || '').trim() ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s ease',
                            minWidth: '50px',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            if (!disabled && (roleInputs['facility-' + selectedFacilityId] || '').trim()) {
                              e.currentTarget.style.backgroundColor = '#FF5533';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!disabled && (roleInputs['facility-' + selectedFacilityId] || '').trim()) {
                              e.currentTarget.style.backgroundColor = '#FF6B47';
                            }
                          }}
                        >
                          Add
                        </button>
                      </Box>
                      
                      {/* Display existing facility-level roles */}
                      {selectedFacilityId && (() => {
                        const facilityAffiliation = affiliations.find(
                          aff => aff.facilityId === selectedFacilityId && (!aff.departmentId || aff.departmentId === '')
                        );
                        const roles = facilityAffiliation?.role || [];
                        const affiliationIndex = affiliations.findIndex(
                          aff => aff.facilityId === selectedFacilityId && (!aff.departmentId || aff.departmentId === '')
                        );
                        
                        console.log('Facility-level roles debug:', {
                          selectedFacilityId,
                          facilityAffiliation,
                          roles,
                          affiliationIndex,
                          allAffiliations: affiliations
                        });
                        
                        if (roles.length === 0) return null;
                        
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                            {roles.map((role, roleIdx) => (
                              <ThemedChip
                                key={roleIdx}
                                variant="default"
                                size="small"
                                onDelete={() => handleRemoveRole(affiliationIndex, role)}
                                customStyle={{
                                  fontSize: '12px',
                                  height: '24px',
                                  padding: '4px 8px'
                                }}
                              >
                                {role}
                              </ThemedChip>
                            ))}
                          </Box>
                        );
                      })()}
                    </Box>
                  ) : (
                    departments
                      .filter(department => department.isActive !== false) // Filter out disabled departments
                      .map((department) => {
                      const isSelected = isDepartmentSelected(department._id);
                      const affiliation = affiliations.find(
                        aff => aff.facilityId === selectedFacilityId && aff.departmentId === department._id
                      );
                      const roles = affiliation?.role || [];
                      const affiliationIndex = affiliations.findIndex(
                        aff => aff.facilityId === selectedFacilityId && aff.departmentId === department._id
                      );

                        return (
                          <Box key={department._id} sx={{ mb: 2.5 }}>
                            {/* Single Row: Checkbox + Name + Input + Add Button */}
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              flexWrap: 'nowrap'
                            }}>
                              {/* Checkbox */}
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleDepartmentToggle(department._id)}
                                disabled={disabled}
                                sx={{
                                  color: currentTheme.text.secondary,
                                  padding: '4px',
                                  '&.Mui-checked': {
                                    color: '#FF6B47',
                                  },
                                }}
                              />
                              
                              {/* Department Name */}
                              <Typography sx={{ 
                                color: currentTheme.text.primary, 
                                fontSize: '14px',
                                fontWeight: isSelected ? 600 : 400,
                                minWidth: '120px',
                                flexShrink: 0
                              }}>
                                {department.name}
                              </Typography>
                              
                              {/* Role Input - Only show when selected */}
                              {isSelected && (
                                <>
                                  <input
                                    type="text"
                                    placeholder="Add role (e.g., Physician, Consultant)"
                                    value={roleInputs[department._id] || ''}
                                    onChange={(e) => setRoleInputs(prev => ({
                                      ...prev,
                                      [department._id]: e.target.value
                                    }))}
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddRoleToDepartment(department._id, roleInputs[department._id] || '');
                                      }
                                    }}
                                    disabled={disabled}
                                    style={{
                                      flex: '1 1 auto',
                                      minWidth: 0,
                                      padding: '8px 10px',
                                      border: `1px solid ${currentTheme.border?.primary || '#ddd'}`,
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      backgroundColor: themeMode === 'dark' ? '#2d2a45' : '#ffffff',
                                      color: currentTheme.text?.primary || '#333',
                                      outline: 'none'
                                    }}
                                  />
                                  <button
                                    onClick={() => handleAddRoleToDepartment(department._id, roleInputs[department._id] || '')}
                                    disabled={disabled || !(roleInputs[department._id] || '').trim()}
                                    style={{
                                      padding: '8px 16px',
                                      backgroundColor: disabled || !(roleInputs[department._id] || '').trim() ? '#ccc' : '#FF6B47',
                                      color: '#ffffff',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      cursor: disabled || !(roleInputs[department._id] || '').trim() ? 'not-allowed' : 'pointer',
                                      transition: 'background-color 0.2s ease',
                                      minWidth: '50px',
                                      flexShrink: 0,
                                      whiteSpace: 'nowrap'
                                    }}
                                    onMouseEnter={(e) => {
                                      if (!disabled && (roleInputs[department._id] || '').trim()) {
                                        e.currentTarget.style.backgroundColor = '#FF5533';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (!disabled && (roleInputs[department._id] || '').trim()) {
                                        e.currentTarget.style.backgroundColor = '#FF6B47';
                                      }
                                    }}
                                  >
                                    Add
                                  </button>
                                </>
                              )}
                            </Box>
                            
                            {/* Existing Roles - Below on next line */}
                            {isSelected && roles.length > 0 && (
                              <Box sx={{ ml: 5, mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {roles.map((role, roleIdx) => (
                                  <ThemedChip
                                    key={roleIdx}
                                    variant="default"
                                    size="small"
                                    onDelete={() => handleRemoveRole(affiliationIndex, role)}
                                    customStyle={{
                                      fontSize: '12px',
                                      height: '24px',
                                      padding: '4px 8px'
                                    }}
                                  >
                                    {role}
                                  </ThemedChip>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                    })
                  )}
                </Box>
              )}
            </Paper>
          </Box>

          {/* Error Display */}
          {(hasError || fieldState.error) && (
            <Typography variant="caption" sx={{ color: '#e63e2b', mt: 1, display: 'block' }}>
              {fieldError?.message || fieldState.error?.message || 'At least one affiliation is required'}
            </Typography>
          )}
        </Box>
      )}
    />
  );
};

FacilityDepartmentAssignment.propTypes = {
  fieldName: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  fieldError: PropTypes.object,
  hasError: PropTypes.bool,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default FacilityDepartmentAssignment;

